/**
 * The one place dates and times are formatted for display.
 *
 * Everything below used to exist two to four times over as private copies in
 * components, and the copies disagreed. `formatWhen` was four functions doing
 * three different things — relative on the Error log and NOS sync tabs,
 * absolute-without-a-year on Email outbox, absolute-with-a-year in the
 * BROWSER's locale on Announcements — so one viewer read the same timestamp
 * three ways across Settings. `formatDateOnly` had forked three ways, and one
 * copy returned null (a blank cell, indistinguishable from missing data) for
 * any value carrying a time. Locales are pinned to en-US throughout: a
 * `undefined` locale renders per-viewer, which is what caused the drift.
 *
 * Date-only helpers cover values that ride on a `timestamptz` column but are
 * conceptually a calendar date (e.g. certification `expires_at`). Such values
 * are written as UTC midnight of the picked day, so `new Date(iso)` +
 * `toLocaleDateString()` shifts the day for any viewer west of UTC, and a raw
 * `getTime() < Date.now()` comparison flags the value "expired" for its entire
 * final day. These helpers treat the value as a pure calendar date so it
 * round-trips to exactly the day that was picked, in any timezone.
 */

/** The calendar-date part (`YYYY-MM-DD`) of a UTC-midnight timestamp. */
function utcDatePart(value: string | Date): string {
  const iso = typeof value === "string" ? value : value.toISOString();
  return iso.slice(0, 10);
}

/** The viewer's local calendar date as `YYYY-MM-DD`. */
export function todayLocal(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Format a UTC-midnight date-only value as e.g. "Jul 8, 2026" (null-safe). */
export function formatDateOnly(value: string | Date | null): string | null {
  if (!value) return null;
  const [y, m, d] = utcDatePart(value).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Format a UTC-midnight date-only value as e.g. "Jul 2026" (null-safe). */
export function formatMonthYear(value: string | Date | null): string | null {
  if (!value) return null;
  const [y, m, d] = utcDatePart(value).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}

/**
 * True once the viewer's local calendar date is strictly past the expiry date.
 * A certification expiring on the 8th stays valid all through the 8th and reads
 * expired starting the 9th (both in the viewer's local calendar).
 */
export function isExpiredDateOnly(value: string | Date | null): boolean {
  if (!value) return false;
  return todayLocal() > utcDatePart(value);
}

// ── Timestamps (date + time) ──────────────────────────────────────

const RELATIVE = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

/** Relative label for a Date — "3 hours ago", "in 2 days". */
export function formatRelative(then: Date): string {
  const seconds = Math.round((then.getTime() - Date.now()) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 60) return RELATIVE.format(seconds, "second");
  if (abs < 3600) return RELATIVE.format(Math.round(seconds / 60), "minute");
  if (abs < 86400) return RELATIVE.format(Math.round(seconds / 3600), "hour");
  if (abs < 2592000) return RELATIVE.format(Math.round(seconds / 86400), "day");
  if (abs < 31536000) return RELATIVE.format(Math.round(seconds / 2592000), "month");
  return RELATIVE.format(Math.round(seconds / 31536000), "year");
}

/**
 * `formatRelative` for a raw ISO string, with an em dash for a null or
 * unparseable value — so a table cell never reads "Invalid Date". This is the
 * form Settings tables want; `formatRelative` is for callers that already hold
 * a Date they know is valid.
 */
export function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "—";
  return formatRelative(then);
}

const DATE_TIME: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

/**
 * Absolute date and time for the `title` attribute beside a `formatWhen`
 * label. Empty string for a null or unparseable value — an empty tooltip is
 * the right answer there, where an em dash would render a stray "—" on hover.
 */
export function formatExact(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", DATE_TIME);
}

/** Absolute date and time as a visible label. Em dash for null/unparseable. */
export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", DATE_TIME);
}

/**
 * `MM/DD/YYYY` from a stored UTC-midnight effective date.
 *
 * Deliberately reads the UTC parts rather than the local ones: an effective
 * date is the calendar day an operator picked, and going through the local
 * calendar shifts it a day for any viewer west of UTC. Returns the input
 * unchanged when it can't be parsed, since the caller has nothing better to show.
 */
export function formatEffectiveDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${m}/${day}/${d.getUTCFullYear()}`;
}
